import { signInWithCustomToken } from 'firebase/auth'
import * as React from 'react'
import { firebaseAuth } from '../../../config/firebase'
import { makeRequest, parseRequestError } from '../../../utils/sso'
import { SignupStep } from '../SignupFlow'
import { AccountSelection } from './AccountSelection'
import { AuthSelection } from './AuthSelection'
import { EmailVerification } from './EmailVerification'
import { getSocialAuth } from '../../../utils/getsocial'
import { SMSVerification } from './SMSVerification'
import { UberPassword } from './UberPassword'
import { UberPhone } from './UberPhone'

export enum SSOStep {
  UberPhone,
  AccountSelection,
  SMSVerification,
  AuthSelection,
  EmailVerification,
  UberPassword,
}

export type SSOStepProps = {
  makeSSORequest: (body: object, defaultErrorMessage: string) => Promise<void>
}

const SSO_STARTING_STEP = SSOStep.UberPhone

type SSOFlowProps = {
  setSignupStep: (step: SignupStep) => void
  setUserPhoneNumber: React.Dispatch<React.SetStateAction<string>>
}

export const SSOFlow = ({
  setSignupStep,
  setUserPhoneNumber,
}: SSOFlowProps): JSX.Element => {
  const [sessionId, setSessionId] = React.useState('')
  const [ssoStep, setSSOStep] = React.useState(SSO_STARTING_STEP)
  const [prevRequest, setPrevRequest] = React.useState({})
  const [emailHints, setEmailHints] = React.useState<string[]>([])
  const [emailHint, setEmailHint] = React.useState('')

  // The order of the SSO steps is indeterminate, so after any intermediary step
  // we must check all cases to determine where to move to next. The `data`
  // argument is the response body from the last SSO request.
  const moveToNextStep = async (data: any) => {
    if (data.stage === 'sign_in' && data.event_type === 'TypeSelectAccount') {
      const emailHints = data.hints.map((hint) => hint.emailHint)
      setEmailHints(emailHints)
      setSSOStep(SSOStep.AccountSelection)
    } else if (data.stage === 'options') {
      // We automatically push the `EmailVerification` screen first. This
      // requires an additional request to the backend.
      const body = {
        session_id: sessionId,
        stage: 'options',
        event_type: 'TypeEmailOTP',
      }
      await makeSSORequest(
        body,
        'Something unexpected happened, please try again later.'
      )
    } else if (data.stage === 'finished') {
      await signInWithCustomToken(firebaseAuth, data.user_token)

      // Once the user has been created in firestore, authenticate them via
      // GetSocial. This is done asynchronously to not block the main flow.
      getSocialAuth()

      setSignupStep(SignupStep.Generating)
    } else if (data.stage === 'sign_in' && data.event_type === 'TypeEmailOTP') {
      setEmailHint(data.hint)
      setSSOStep(SSOStep.EmailVerification)
    } else if (data.stage === 'sign_in' && data.event_type === 'TypeSMSOTP') {
      setSSOStep(SSOStep.SMSVerification)
    } else {
      console.error('Invalid SSO State!')
    }
  }

  const resendLastRequest = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
    await makeRequest(prevRequest, setPrevRequest)
  }

  // Errors will propagate up, so the caller must handle them appropriately.
  const makeSSORequest = async (body: object, defaultErrorMessage: string) => {
    const response = await makeRequest(body, setPrevRequest)
    const data = await response.json()

    if (response.ok) {
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id)
      }
      await moveToNextStep(data)
    } else {
      const errorMessage = parseRequestError(
        response.status,
        data,
        defaultErrorMessage,
        emailHint
      )
      throw new Error(errorMessage)
    }
  }

  switch (ssoStep) {
    case SSOStep.UberPhone:
      return (
        <UberPhone
          makeSSORequest={makeSSORequest}
          setSignupStep={setSignupStep}
          setUserPhoneNumber={setUserPhoneNumber}
        />
      )
    case SSOStep.AccountSelection:
      return (
        <AccountSelection
          sessionId={sessionId}
          emailHints={emailHints}
          makeSSORequest={makeSSORequest}
        />
      )
    case SSOStep.SMSVerification:
      return (
        <SMSVerification
          sessionId={sessionId}
          onResendRequest={resendLastRequest}
          makeSSORequest={makeSSORequest}
        />
      )
    case SSOStep.AuthSelection:
      return (
        <AuthSelection
          setSSOStep={setSSOStep}
          sessionId={sessionId}
          makeSSORequest={makeSSORequest}
        />
      )
    case SSOStep.EmailVerification:
      return (
        <EmailVerification
          setSSOStep={setSSOStep}
          sessionId={sessionId}
          emailHint={emailHint}
          makeSSORequest={makeSSORequest}
        />
      )
    case SSOStep.UberPassword:
      return (
        <UberPassword sessionId={sessionId} makeSSORequest={makeSSORequest} />
      )
  }
}
